import { FeeSummary, MonthDropdown } from "../components/PortalFees";
import { fmtDay } from "../lib/reminders";
import { paymentTotal } from "../lib/payments";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, CalendarCheck, IndianRupee, Trophy, MessageSquare, Send, MapPin, Clock, Calendar as CalIcon, Dumbbell, Shield, FileText } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth/AuthContext";
import { ReportView } from "../components/ReportView";
import { ChangePassword } from "../components/ChangePassword";
import { api, inr, monthLabel, formatApiError, FEE_LABELS } from "../lib/api";
import { PortalDocs, DocsDisclaimer, PortalWorkouts, PortalPolicies } from "../components/PortalSections";
import { UpgradeOffer } from "../components/UpgradeOffer";

const MARK_STYLE = {
  P: "bg-[#D4AF37] text-black",
  A: "bg-red-500/80 text-white",
  T: "bg-blue-500/80 text-white",
  "-": "bg-white/10 text-white/50",
};

const FEEDBACK_CATEGORIES = ["Coaching", "Facilities", "Communication", "Fees & Billing", "Suggestion", "Other"];

const FeedbackForm = () => {
  const [category, setCategory] = useState(FEEDBACK_CATEGORIES[0]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/feedback", { category, message });
      toast.success("Thanks! Your feedback reached the academy.");
      setMessage("");
      setSent(true);
      setTimeout(() => setSent(false), 4000);
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  return (
    <form onSubmit={submit} className="border border-white/10 bg-[#0A0A0A] p-6" data-testid="feedback-form">
      <div className="grid sm:grid-cols-[200px_1fr] gap-3 mb-3">
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          className="bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-[#D4AF37]"
          data-testid="feedback-category">
          {FEEDBACK_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea required minLength={5} rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
          placeholder="Share your feedback, questions, or suggestions…"
          className="bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]"
          data-testid="feedback-message" />
      </div>
      <div className="flex items-center gap-4">
        <button type="submit" disabled={sending} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-50" data-testid="feedback-submit">
          <Send size={13} /> {sending ? "Sending..." : "Submit Feedback"}
        </button>
        {sent && <span className="text-[#D4AF37] text-xs uppercase tracking-widest" data-testid="feedback-sent">✓ Received</span>}
      </div>
    </form>
  );
};

const TournamentsList = ({ tournaments }) => {
  if (tournaments.length === 0) {
    return (
      <div className="border border-white/10 bg-[#0A0A0A] p-8 text-center" data-testid="portal-tournaments-empty">
        <Trophy size={32} className="text-white/20 mx-auto mb-3" />
        <p className="text-white/50 text-sm">No upcoming tournaments announced yet. Stay tuned.</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="portal-tournaments-list">
      {tournaments.map((t) => (
        <div key={t.id} className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`portal-tournament-${t.id}`}>
          <h3 className="font-heading font-black text-lg uppercase tracking-tight">{t.name}</h3>
          {t.team_name && !Array.isArray(t.age_groups) && <p className="text-[#D4AF37] text-xs mt-1">Team: {t.team_name}</p>}
          <div className="space-y-1.5 text-xs text-white/60 mt-3">
            <p className="flex items-center gap-2"><CalIcon size={12} className="text-[#D4AF37]" />
              {(() => {
                const s = t.start_date || t.date;
                const e = t.end_date;
                if (!s) return "";
                const fmt = (d) => new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" });
                return !e || e === s ? fmt(s) : `${fmt(s)} — ${fmt(e)}`;
              })()}
            </p>
            {Array.isArray(t.age_groups) && t.age_groups.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {t.age_groups.map((a) => <span key={a} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37]">{a}</span>)}
              </div>
            )}
            {t.timings && <p className="flex items-center gap-2"><Clock size={12} className="text-[#D4AF37]" /> {t.timings}</p>}
            {t.location && <p className="flex items-center gap-2"><MapPin size={12} className="text-[#D4AF37]" /> {t.location}</p>}
            {t.registration_fee > 0 && <p className="text-[#D4AF37] font-semibold pt-2">Registration Fee: {inr(t.registration_fee)}</p>}
            {t.notes && <p className="text-white/50 pt-2 border-t border-white/5 mt-2 whitespace-pre-wrap">{t.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Portal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [reports, setReports] = useState([]);
  const [tournaments, setTournaments] = useState([]);

  useEffect(() => {
    api.get("/portal/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/portal/reports").then((r) => setReports(r.data)).catch(() => {});
    api.get("/portal/tournaments").then((r) => setTournaments(r.data)).catch(() => {});
  }, []);

  const reloadPlayer = () => api.get("/portal/me").then((r) => setData(r.data)).catch(() => {});

  if (!data) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><p className="text-[#D4AF37] text-xs tracking-[0.3em] uppercase animate-pulse">Loading...</p></div>;
  }

  const { player, month, dues, payments, attendance, attendance_rate } = data;

  return (
    <main className="min-h-screen bg-[#050505] text-white noise-overlay relative" data-testid="portal-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 pt-28">
        <div className="flex items-start justify-between mb-10">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Member Portal</p>
            <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight mt-2" data-testid="portal-player-name">{player.name}</h1>
            <p className="text-white/50 text-sm mt-1">{player.player_id} · {player.batch || "Batch TBD"}</p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className="text-white/40 text-xs">{user?.email}</span>
            <div className="flex items-center gap-4">
              <ChangePassword testid="portal-change-password" />
              <button onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-2 text-white/50 hover:text-[#D4AF37] text-xs uppercase tracking-widest transition-colors duration-200" data-testid="portal-logout">
                <LogOut size={14} /> Logout
              </button>
            </div>
          </div>
        </div>

        <DocsDisclaimer documents={player.documents} />

        <div className="grid sm:grid-cols-2 gap-px bg-white/10 border border-white/10 mb-10">
          <div className="bg-[#0A0A0A] p-6" data-testid="portal-profile">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4">Profile</p>
            <div className="space-y-2 text-sm">
              <p className="flex justify-between"><span className="text-white/50">Centre</span><span className="text-right">{player.centre || "—"}</span></p>
              <p className="flex justify-between"><span className="text-white/50">Batch</span><span className="text-right">{player.batch || "—"}</span></p>
              <p className="flex justify-between"><span className="text-white/50">Status</span><span className="text-[#D4AF37]">{player.status}</span></p>
              <p className="flex justify-between"><span className="text-white/50">Joined</span><span>{player.joining_date || "—"}</span></p>
            </div>
          </div>
          <div className="bg-[#0A0A0A] p-6" data-testid="portal-attendance-rate">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-4 flex items-center gap-2"><CalendarCheck size={13} className="text-[#D4AF37]" /> Attendance Rate</p>
            <p className="font-heading text-5xl font-black text-[#D4AF37]">{attendance_rate === null ? "—" : `${attendance_rate}%`}</p>
            <p className="text-white/40 text-xs mt-2">{attendance.length} session{attendance.length === 1 ? "" : "s"} recorded</p>
          </div>
        </div>

        <FeeSummary s={data.fee_summary} />

        <UpgradeOffer upgrade={data.upgrade} onRequested={reloadPlayer} />

        <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2">
          <Trophy size={13} className="text-[#D4AF37]" /> Upcoming Tournaments
        </h2>
        <div className="mb-10">
          <TournamentsList tournaments={tournaments} />
        </div>

        <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4">Quarterly Skill Reports</h2>
        <div className="mb-10" data-testid="portal-reports">
          {reports.length === 0 && (
            <div className="border border-white/10 bg-[#0A0A0A] p-8 text-center" data-testid="portal-reports-waiting">
              <p className="font-heading text-lg font-bold text-white/80">Your report is on its way.</p>
              <p className="text-white/50 text-sm mt-2 max-w-md mx-auto leading-relaxed">
                Our team is working on it behind the scenes — please wait patiently. The moment your quarterly assessment is published, it will appear right here.
              </p>
            </div>
          )}
          <div className="space-y-4">
            {reports.map((r) => (
              <ReportView key={r.id} data={r} testid={`portal-report-${r.quarter}`} />
            ))}
          </div>
        </div>

        <div className="mb-10">
          <MonthDropdown title="Payment History" rows={payments} testid="portal-payments" empty="No payments recorded yet."
            render={(p) => (
              <div key={p.id} className="flex justify-between text-sm border-b border-white/5 last:border-0 p-4 bg-[#050505]">
                <span className="text-white/60">{fmtDay(p.date)} · {p.mode}</span>
                <span className="text-[#D4AF37] font-semibold">{inr(paymentTotal(p))}</span>
              </div>
            )} />
          <MonthDropdown title="Attendance" rows={attendance} testid="portal-attendance" empty="No sessions recorded yet."
            render={(a) => (
              <div key={`${a.player_id}-${a.date}`} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 p-4 bg-[#050505]">
                <span className="text-white/60">{fmtDay(a.date)} · {a.batch}</span>
                <span className={`w-8 h-8 flex items-center justify-center font-heading font-black text-xs rounded-sm ${MARK_STYLE[a.mark] || MARK_STYLE["-"]}`}>{a.mark === "-" ? "–" : a.mark}</span>
              </div>
            )} />
        </div>

        <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2"><Dumbbell size={13} className="text-[#D4AF37]" /> Training Workouts</h2>
        <div className="mb-10">
          <PortalWorkouts />
        </div>

        <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2"><FileText size={13} className="text-[#D4AF37]" /> Your Documents</h2>
        <div className="mb-10">
          <PortalDocs playerId={player.player_id} documents={player.documents} onChanged={reloadPlayer} />
        </div>

        <div className="mb-10">
          <PortalPolicies />
        </div>

        <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2">
          <MessageSquare size={13} className="text-[#D4AF37]" /> Share Feedback with the Academy
        </h2>
        <div className="mb-10">
          <FeedbackForm />
        </div>
      </div>
    </main>
  );
}
